import Text from './text';
import type { TOC } from '@ember/component/template-only';

// ---------------------------------------------------------------------------
// PipelineStage – a single stage in a pipeline flow visualization
// ---------------------------------------------------------------------------

/**
 * Color theme for the stage.
 *
 * The 5 names stay in the type for API back-compat with the existing
 * <PipelineFlow> callers, but each resolves to a TIO canonical recipe
 * via the `.app-pipeline-stage--<color>` class family (see
 * `addons/tio-ui/src/styles/`).
 */
export type PipelineStageColor = 'violet' | 'teal' | 'green' | 'gray' | 'amber';
export type PipelineStageSize = 'sm' | 'md' | 'lg';

interface PipelineStageSignature {
  Args: {
    /** The numeric or string value to display */
    value: string | number;
    /** Label text below the value */
    label: string;
    /** Color theme for the stage */
    color?: PipelineStageColor;
    /** Size variant - sm for compact, md for default, lg for bordered */
    size?: PipelineStageSize;
  };
  Element: HTMLDivElement;
}

/**
 * PipelineStage - A single stage in a pipeline flow visualization
 *
 * Displays a value and label in a colored box, typically used within a
 * horizontal flow with arrows between stages.
 *
 * Styling lives in the `.app-pipeline-stage` CSS class family — see
 * `addons/tio-ui/src/styles/`. The component itself just
 * composes the right modifier classes from the @color and @size args.
 *
 * @example
 * ```gts
 * import PipelineStage from 'tio-ui/components/pipeline-stage';
 *
 * <PipelineStage @value={{85}} @label="In Pipeline" @color="violet" />
 * ```
 */
const PipelineStage: TOC<PipelineStageSignature> = <template>
  {{#let (if @color @color "violet") as |color|}}
    {{#let (if @size @size "md") as |size|}}
      <div
        data-test-pipeline-stage
        class="app-pipeline-stage app-pipeline-stage--{{size}} app-pipeline-stage--{{color}}"
        ...attributes
      >
        <div data-test-pipeline-stage-value class="app-pipeline-stage-value">
          {{@value}}
        </div>
        <Text data-test-pipeline-stage-label @variant="muted" @size="sm">{{@label}}</Text>
      </div>
    {{/let}}
  {{/let}}
</template>;

export default PipelineStage;

export type { PipelineStageSignature };
